import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { profileCropRect, exportProfileImage } from './profileEditModel';
export default function ProfileImageCrop({ file, ratio, en, onCancel, onUse }) {
  const [source, setSource] = useState(''),
    [image, setImage] = useState(null),
    [position, setPosition] = useState([0.5, 0.5, 1]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  const alive = useRef(false),
    lock = useRef(false);
  const id = React.useId();
  useEffect(() => {
    alive.current = true;
    const url = URL.createObjectURL(file);
    setSource(url);
    return () => {
      alive.current = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);
  const useImage = async () => {
    if (!image || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(false);
    try {
      const result = await exportProfileImage(image, ratio, position);
      if (alive.current) onUse(result);
    } catch {
      if (alive.current) setError(true);
    } finally {
      if (alive.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const r = image && profileCropRect(image.naturalWidth, image.naturalHeight, ratio, ...position);
  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      className="pp-review-dialog"
      aria-labelledby={id}
      onClose={() => {
        if (!busy) onCancel();
      }}
    >
      <DialogTitle id={id}>{en ? 'Crop photo' : '사진 자르기'}</DialogTitle>
      <DialogContent>
        <div className="pp-profile-crop-stage" style={{ aspectRatio: ratio }}>
          {source && (
            <img
              src={source}
              alt={en ? 'Crop preview' : '자르기 미리보기'}
              onLoad={(e) => setImage(e.currentTarget)}
              onError={() => setError(true)}
              style={
                r
                  ? {
                      width: `${(image.naturalWidth / r.width) * 100}%`,
                      height: `${(image.naturalHeight / r.height) * 100}%`,
                      left: `${(-r.x / r.width) * 100}%`,
                      top: `${(-r.y / r.height) * 100}%`,
                    }
                  : { visibility: 'hidden' }
              }
            />
          )}
        </div>
        {[
          en ? 'Horizontal position' : '가로 위치',
          en ? 'Vertical position' : '세로 위치',
          en ? 'Zoom' : '확대',
        ].map((label, i) => (
          <label className="pp-profile-crop-control" key={label}>
            {label}
            <input
              type="range"
              min={i === 2 ? 1 : 0}
              max={i === 2 ? 3 : 1}
              step=".01"
              value={position[i]}
              disabled={busy}
              onChange={(e) =>
                setPosition((p) => p.map((v, j) => (j === i ? Number(e.target.value) : v)))
              }
            />
          </label>
        ))}
        {error && (
          <p role="alert">
            {en
              ? 'Could not process this image. Try another photo.'
              : '이미지를 처리하지 못했어요. 다른 사진을 선택해 주세요.'}
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onCancel}>
          {en ? 'Cancel' : '취소'}
        </Button>
        <Button disabled={!image || busy} onClick={useImage}>
          {en ? 'Use photo' : '사진 사용'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
